<template>
  <section
    class="obituary-single__current-events container"
    v-if="cards.length"
    :class="{
      'is-running': rotation.running.value,
      'is-waiting': rotation.waiting.value,
    }"
    :ref="rotation.el"
    :style="{ '--highlights-duration': `${ROTATION_MS}ms` }"
  >
    <div class="obituary-single__current-events-wrapper">
      <div class="highlights__head">
        <h2 class="highlights__title" v-text="texts.heading" />

        <div
          class="highlights__chips"
          v-if="cards.length > 1"
          :ref="swipe.chips"
        >
          <button
            class="highlights__chip"
            type="button"
            v-for="(card, i) in cards"
            :key="card"
            :aria-controls="`highlights-card-${card}`"
            :aria-current="i === active ? 'true' : undefined"
            :class="{ 'is-active': i === active }"
            @click="rotation.go(i)"
          >
            <i
              aria-hidden="true"
              class="highlights__chip-fill"
              v-if="i === active"
              :key="rotation.cycle.value"
            />
            <span v-text="chipLabel(card)" />
          </button>

          <button
            class="highlights__pause"
            type="button"
            v-if="rotation.rotates.value && !swipe.active.value"
            :aria-label="rotation.paused.value ? texts.play : texts.pause"
            @click="rotation.toggle()"
          >
            <slideshow-icon :name="rotation.paused.value ? 'play' : 'pause'" />
          </button>
        </div>
      </div>

      <div
        class="highlights__stack"
        :aria-live="rotation.running.value ? 'off' : 'polite'"
        :ref="swipe.track"
      >
        <div
          class="highlights__slide"
          v-for="(card, i) in cards"
          :key="card"
          :class="{ 'is-active': i === active }"
          :id="`highlights-card-${card}`"
          :inert="i !== active && !swipe.active.value ? true : undefined"
        >
          <flowers-card
            v-if="card === 'flowers'"
            :deadline="highlights.flowersDeadline"
            :family-url="highlights.familyFlowersUrl"
            :image="highlights.images.flowers"
            :now="now"
          />
          <family-flowers-card
            v-else-if="card === 'family_flowers'"
            :image="highlights.images.family_flowers"
            :url="highlights.familyFlowersUrl"
          />
          <memorial-gift-card
            v-else-if="card === 'memorial_gift'"
            :deceased-name="deceasedName"
            :memorial-fund="memorialFund"
          />
          <candle-card
            v-else-if="card === 'candle' && highlights.candle"
            :candle="highlights.candle"
            :deceased-name="deceasedName"
          />
          <book-upcoming-card
            v-else-if="card === 'book_upcoming' && bookPanel.upcoming"
            :deceased-name="deceasedName"
            :now="now"
            :time-zone="highlights.timeZone"
            :upcoming="bookPanel.upcoming"
          />
          <photos-card
            v-else-if="card === 'photos'"
            :deceased-name="deceasedName"
            :image="highlights.images.photos"
          />
          <slideshow-card
            v-else-if="card === 'slideshow'"
            :deceased-name="deceasedName"
          />
          <life-story-card v-else-if="card === 'life_story'" />
          <memory-book-card
            v-else-if="card === 'memory_book' && bookPanel.book"
            :book="bookPanel.book"
            :deceased-name="deceasedName"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
  import {
    computed,
    defineComponent,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import {
    hasSlideshow,
    lifeStoryOnPage,
    slideshowTexts,
  } from "../../composables/useSlideshow";
  import {
    type CardId,
    currentCards,
    pagePhase,
    parseHighlights,
  } from "../../highlights/cards";
  import {
    bookPanel,
    loadMemoryBook,
  } from "../../highlights/memory-book-panel";
  import { highlightTexts, memoryBookTexts } from "../../highlights/texts";
  import { useHighlightTracking } from "../../highlights/use-highlight-tracking";
  import { ROTATION_MS, useRotation } from "../../highlights/use-rotation";
  import { useSwipeTrack } from "../../highlights/use-swipe-track";
  import SlideshowIcon from "../Slideshow/SlideshowIcon.vue";
  import BookUpcomingCard from "./cards/BookUpcomingCard.vue";
  import CandleCard from "./cards/CandleCard.vue";
  import FamilyFlowersCard from "./cards/FamilyFlowersCard.vue";
  import FlowersCard from "./cards/FlowersCard.vue";
  import LifeStoryCard from "./cards/LifeStoryCard.vue";
  import MemorialGiftCard from "./cards/MemorialGiftCard.vue";
  import MemoryBookCard from "./cards/MemoryBookCard.vue";
  import PhotosCard from "./cards/PhotosCard.vue";
  import SlideshowCard from "./cards/SlideshowCard.vue";

  const CLOCK_MS = 60_000;

  function parseFund(raw: string | undefined) {
    try {
      return JSON.parse(raw ?? "") || null;
    } catch {
      return null;
    }
  }

  export default defineComponent({
    components: {
      BookUpcomingCard,
      CandleCard,
      FamilyFlowersCard,
      FlowersCard,
      LifeStoryCard,
      MemorialGiftCard,
      MemoryBookCard,
      PhotosCard,
      SlideshowCard,
      SlideshowIcon,
    },
    name: "CurrentEvents",

    setup() {
      const mountEl = getCurrentInstance()?.proxy?.$mountEl as
        | HTMLElement
        | undefined;
      const dataset: DOMStringMap = mountEl?.dataset ?? {};
      const store = useStore();
      const texts = highlightTexts();
      const highlights = parseHighlights(dataset.highlights);

      // One clock for the selection and the countdown, so a cached page moves from
      // flowers to the ceremony to flowers to the family when the deadline passes.
      const now = ref(new Date());
      const slideshow = hasSlideshow();
      const lifeStory = lifeStoryOnPage();

      const cards = computed(() =>
        currentCards({
          bookForSale: !!bookPanel.book,
          bookUpcoming: !!bookPanel.upcoming,
          hasSlideshow: slideshow,
          highlights,
          lifeStory,
          now: now.value,
        })
      );

      const rotation = useRotation(() => cards.value.length);
      const active = computed(() => rotation.index.value);
      const swipe = useSwipeTrack(rotation);
      useHighlightTracking(cards, rotation, () =>
        pagePhase(highlights.ceremonyDate, now.value, highlights.timeZone)
      );

      const slideshowCopy = slideshowTexts();
      const chips: Record<CardId, string> = {
        book_upcoming: memoryBookTexts().chip,
        candle: texts.candleChip,
        family_flowers: texts.flowersChip,
        flowers: texts.flowersChip,
        life_story: slideshowCopy.lifeTitle,
        memorial_gift: texts.giftChip,
        memory_book: memoryBookTexts().chip,
        photos: texts.photosChip,
        slideshow: slideshowCopy.heading,
      };

      watch(
        () => cards.value.includes("memorial_gift"),
        (hasGift) => {
          if (hasGift) {
            store
              .dispatch("donations/getTotalDonations", dataset.obituaryId)
              .catch(() => undefined);
          }
        },
        { immediate: true }
      );

      // The page is cached, so whether the book is for sale, or on its way, is asked from here.
      if (highlights.memoryBook && dataset.obituaryId) {
        loadMemoryBook(dataset.obituaryId);
      }

      let clock: ReturnType<typeof setInterval> | undefined;
      onMounted(() => {
        clock = setInterval(() => {
          now.value = new Date();
        }, CLOCK_MS);
      });
      onBeforeUnmount(() => clearInterval(clock));

      return {
        active,
        bookPanel,
        cards,
        chipLabel: (card: CardId) => chips[card] ?? "",
        deceasedName: dataset.deceasedNickname ?? "",
        highlights,
        memorialFund: parseFund(dataset.memorialFund),
        now,
        ROTATION_MS,
        rotation,
        swipe,
        texts,
      };
    },
  });
</script>
